import React, { useEffect, useMemo, useState } from 'react';
import AuthLayout from '../../components/layout/AuthLayout';
import { useGetLeadsStatusesQuery, useLazyGetLeadsQuery } from '../../api/leads.js';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { Link } from 'react-router-dom';
import { debounce } from 'lodash';
import BaseDropdown from '../../components/form/BaseDropdown.jsx';
import IconBulb from '../../components/images/IconBulb.jsx';
import IconChevronRight from '../../components/images/IconChevronRight.jsx';

const Leads = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [leadsResponse, setLeadsResponse] = useState({});
    const [leads, setLeads] = useState([]);
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(1);
    const [hasMorePages, setHasMorePages] = useState(true);
    const [append, setAppend] = useState(false);

    const resetLeadsState = () => {
        setPage(1);
        setAppend(false);
        setHasMorePages(true);
        setLeads([]);
    };

    const resetStatus = () => {
        setStatus('');
        resetLeadsState();
    };

    const searchByStatus = (value) => {
        const newStatus = typeof value === 'object' && value !== null ? value.value : value;
        setStatus(newStatus);
        setPage(1);
    };

    const requestParams = useMemo(() => {
        return {
            status,
            page,
            per_page: 10
        };
    }, [status, page]);

    const [getLeads] = useLazyGetLeadsQuery();
    const { data: statusesData, isLoading: statusesLoading } = useGetLeadsStatusesQuery();

    const statusOptions = useMemo(() => {
        return Array.isArray(statusesData?.response?.lead_statuses)
            ? statusesData.response.lead_statuses.map((statusItem) => ({
                  label: statusItem.label,
                  value: statusItem.value
              }))
            : [];
    }, [statusesData]);

    useEffect(() => {
        setIsLoading(true);
        (async () => {
            const result = await getLeads(requestParams);
            setLeadsResponse(result.data);
        })();
    }, [getLeads, requestParams]);

    useEffect(() => {
        if (leadsResponse?.response) {
            const { response } = leadsResponse;
            if (append) {
                setLeads((prev) => [...prev, ...response.leads]);
            } else {
                setLeads(response.leads);
            }
            setHasMorePages(response.has_more_pages);
            setIsLoading(false);
            setLeadsResponse({});
            setAppend(false);
        }
    }, [leadsResponse, append]);

    useEffect(() => {
        let processing = false;
        const handleScroll = debounce(() => {
            if (
                Math.round(window.innerHeight + document.documentElement.scrollTop) + 10 >=
                document.documentElement.offsetHeight
            ) {
                if (hasMorePages && !isLoading && !processing) {
                    processing = true;
                    setIsLoading(true);
                    setPage((prevPage) => prevPage + 1);
                    setAppend(true);
                    setTimeout(() => {
                        processing = false;
                    }, 500);
                }
            }
        }, 300);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            handleScroll.cancel();
        };
    }, [hasMorePages, isLoading]);

    const renderLeadTitle = (lead) => {
        let fromText = '';
        let toText = '';

        if (lead.from_model === 'Organization') {
            fromText = lead.from_organization?.name || '';
        } else if (lead.from_model === 'Project') {
            fromText = lead.from_project?.name || '';
        } else if (lead.from_model === 'Contact') {
            const contact = lead.from_contact;
            fromText = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') : '';
        }

        if (lead.to_model === 'Organization') {
            toText = lead.to_organization?.name || '';
        } else if (lead.to_model === 'Project') {
            toText = lead.to_project?.name || '';
        } else if (lead.to_model === 'Contact') {
            const contact = lead.to_contact;
            toText = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') : '';
        }

        return `${fromText}${toText ? ' & ' + toText : ''}`;
    };

    return (
        <AuthLayout title='Leads' backRoute='/dashboard'>
            <div className='max-w-3xl w-full mx-auto px-8 mt-6 mb-6'>
                <div className='flex justify-between'>
                    <div className='flex-grow'>
                        <BaseDropdown
                            name='status'
                            placeholder='Select Status'
                            value={status}
                            setValue={searchByStatus}
                            options={statusOptions}
                            disabled={statusesLoading}
                        />
                    </div>
                    <div className='text-theme1-primary2 cursor-pointer ml-4 mt-2' onClick={resetStatus}>
                        Reset
                    </div>
                </div>
            </div>
            <div className='px-8 max-w-3xl w-full mx-auto mb-6'>
                <ul>
                    {leads.map((lead) => (
                        <li key={lead.id} className='flex items-center justify-between'>
                            <div className='w-11 h-11 bg-theme1-gold rounded-xl flex items-center justify-center flex-shrink-0'>
                                <IconBulb className='stroke-white w-8 h-8' />
                            </div>
                            <Link
                                to={`/leads/${lead.id}`}
                                className='flex-grow border-b border-solid border-theme1-tanTransparent ml-4 py-4 flex items-center justify-between'
                            >
                                <div>
                                    <div className='text-[15px] font-semibold mb-[6px] leading-[18px]'>
                                        {renderLeadTitle(lead)}
                                    </div>
                                    <span className='bg-theme1-success rounded-lg text-xs font-bold text-white p-2 py-1 inline-block'>
                                        {lead.ranking} pts.
                                    </span>
                                </div>
                                <IconChevronRight className='w-6 h-6 ml-2 fill-theme1-text3' />
                            </Link>
                        </li>
                    ))}
                </ul>
                {isLoading && <ThreeDotsLoader />}
            </div>
        </AuthLayout>
    );
};

export default Leads;
